import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { getStyleStrippedText } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import {
  TickLargeOutlined,
  CloseCircleFilled,
  TickCircleFilled,
  CircleOutlined,
} from "@toddle-edu/ds-icons";
import { Checkbox } from "@toddle-edu/ds-web";
import { colors } from "Constants";
import TextViewWrapper from "Worksheet/components/TextViewWrapper";

import {
  McqModes,
  customTextStyle,
  customTextStyleV2,
} from "Worksheet/modules/WorksheetConstants";

import classes from "./OptionsList.scss";

const AnswerIconComponent = ({ isSuccess = false }) => {
  return (
    <div className={classes.iconContainer}>
      {isSuccess ? (
        <TickLargeOutlined variant="success" size="xx-small" />
      ) : null}
    </div>
  );
};

const OptionsList = props => {
  const {
    options,
    mode,
    updateInputField,
    fieldKey,
    isAiGeneratedField,
    allowMultiSelect,
    showDocEditor,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState([]);

  const showCorrectAnswer = _.isEqual("evaluate", mode);
  const showCheckBox = _.includes(["edit", "view", "evaluate"], mode);

  const toggleInputSelection = ({ id, updatedValue }) => {
    if (!_.includes(["edit", "teacher-preview"], mode)) return;
    if (_.isEqual(mode, "teacher-preview")) {
      setSelectedOptions(prev => {
        if (allowMultiSelect) {
          return _.xor(prev, [id]);
        }
        return _.includes(prev, id) ? [] : [id];
      });
    }

    const updatedOptions = options.map(option => {
      const isCurrent = _.isEqual(option?.id, id);
      if (allowMultiSelect) {
        if (isCurrent) {
          return { ...option, isStudentSelected: updatedValue };
        }
        return option;
      } else {
        return { ...option, isStudentSelected: isCurrent && updatedValue };
      }
    });

    updateInputField({
      params: {
        [fieldKey]: updatedOptions,
      },
      isDebounce: true,
      isMutation: true,
    });
  };

  const getCheckBoxSelectedState = ({ isStudentSelected, isSelected, id }) => {
    switch (mode) {
      case "preview":
        return isSelected;
      case "view":
      case "edit":
      case "evaluate":
        return isStudentSelected;
      case "teacher-preview":
        return _.includes(selectedOptions, id);
      default:
        return null;
    }
  };

  return (
    <div className={classes.container} role="group">
      {options.map(
        ({ id, value, isStudentSelected, isSelected, isCorrect }) => {
          const fieldValue = getStyleStrippedText({
            text: value,
            type: "DOCUMENT_EDITOR",
          });

          const isCheckBoxSelected = getCheckBoxSelectedState({
            isStudentSelected,
            isSelected,
            id,
          });

          const isInactiveQuestionRevamp = _.includes(
            ["preview", "restricted-view"],
            mode
          );

          const itemContainerClasses = classNames({
            [classes.selectedItemContainer]:
              _.includes(["edit", "view", "teacher-preview"], mode) &&
              isCheckBoxSelected,
            [classes.hoverItemStyle]: _.includes(
              ["edit", "teacher-preview"],
              mode
            ),
            [classes.itemContainer]: !isInactiveQuestionRevamp,
            [classes.itemContainerV2]: isInactiveQuestionRevamp,
          });
          const shouldUpdateStyles = mode == "evaluate" && isCheckBoxSelected;
          const checkBoxColor = isCorrect
            ? colors.borderSuccess
            : colors.borderCritical;

          const checkBoxStyles = shouldUpdateStyles
            ? {
                background: checkBoxColor,
              }
            : {};

          const circleOutlinedClasses = classNames(
            classes.iconDisabled,
            classes.flexShrink0
          );

          return (
            <div
              className={itemContainerClasses}
              style={
                shouldUpdateStyles
                  ? {
                      borderColor: checkBoxColor,
                    }
                  : null
              }
              key={id}
              onClick={() => {
                toggleInputSelection({
                  id,
                  updatedValue: !isStudentSelected,
                });
              }}
              onKeyDown={e =>
                onEnterOrSpaceKeyDown(e, () =>
                  toggleInputSelection({ id, updatedValue: !isStudentSelected })
                )
              }
              role="checkbox"
              aria-checked={isCheckBoxSelected}
              tabIndex={0}
              data-test-id={"worksheet-optionsList-item"}
            >
              <div className={classes.leftContainer}>
                {showCheckBox &&
                isCheckBoxSelected &&
                mode == "evaluate" &&
                !isCorrect ? (
                  <CloseCircleFilled
                    variant="critical"
                    style={{ minWidth: "20px" }}
                  />
                ) : !_.includes(["preview", "restricted-view"], mode) ? (
                  <Checkbox
                    isChecked={isCheckBoxSelected}
                    _legacy_size={"xx-small"}
                    size="medium"
                    dsVersion="2.0"
                    disabled={mode == "evaluate" && !isStudentSelected}
                    color={checkBoxStyles}
                    inputClassName={classes.checkBoxInput}
                    tabIndex={-1}
                    testId={`worksheet-optionsList-itemCheckbox-${_.camelCase(
                      value
                    )}`}
                  />
                ) : isInactiveQuestionRevamp ? (
                  isCheckBoxSelected ? (
                    <TickCircleFilled
                      size="x-small"
                      variant="success"
                      className={classes.flexShrink0}
                    />
                  ) : (
                    <CircleOutlined
                      size="x-small"
                      className={circleOutlinedClasses}
                    />
                  )
                ) : null}
                <div className={classes.richTextViewContainer}>
                  {fieldValue ? (
                    <div className={classes.textContainerView}>
                      <TextViewWrapper
                        value={fieldValue}
                        isAiGeneratedField={isAiGeneratedField}
                        customTextStyle={
                          isInactiveQuestionRevamp
                            ? customTextStyleV2
                            : customTextStyle
                        }
                        mode={"view"}
                        isInactiveQuestionRevamp={isInactiveQuestionRevamp}
                        showDocEditor={showDocEditor}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>

              {showCorrectAnswer && (isCheckBoxSelected || isCorrect) ? (
                <AnswerIconComponent
                  isSuccess={mode == "evaluate" ? isCorrect : true}
                />
              ) : null}
            </div>
          );
        }
      )}
    </div>
  );
};

OptionsList.displayName = "OptionsList";

OptionsList.propTypes = {
  options: PropTypes.array,
  fieldKey: PropTypes.string,
  updateInputField: PropTypes.func,
  mode: PropTypes.oneOf(McqModes),
  allowMultiSelect: PropTypes.bool,
};

OptionsList.defaultProps = {
  options: [],
  fieldKey: "",
  allowMultiSelect: true,
};

export default OptionsList;
